import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { loginSchema, type LoginInput } from '@proref/schemas';
import { useAuth } from '../contexts/auth-context';
import { notifyError, notifySuccess } from '../lib/toast';
import { Logo } from '../components/shared/Logo';
import { CourtArt } from '../components/shared/CourtArt';
import { Icon } from '../components/shared/Icons';
import { Input } from '../components/shared/Input';
import { Field } from '../components/shared/Field';
import { Button } from '../components/shared/Button';
import { Spinner } from '../components/shared/Spinner';
import { PartnerLogos } from '../components/shared/PartnerLogos';
import { LOGIN_PARTNERS } from '../lib/partners';

const SPORTS = ['Handball', 'Football', 'Basketball', 'Volleyball'];

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  // Already signed in (e.g. navigated to /login by hand) → bounce to the app.
  if (user) return <Navigate to="/" replace />;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login(values);
      // Outcome reported via toast; field errors still render inline below inputs.
      notifySuccess('Signed in');
      navigate('/', { replace: true });
    } catch (err) {
      notifyError(err, 'Could not sign you in');
    }
  });

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      {/* Art panel (hidden on small screens) */}
      <div
        className="relative hidden flex-col justify-between overflow-hidden p-9 md:flex"
        style={{ background: 'oklch(0.20 0.04 250)', color: 'oklch(0.96 0.005 85)' }}
      >
        <CourtArt className="pointer-events-none absolute -inset-[10%_20%] h-[120%] w-[140%]" opacity={0.12} stroke="oklch(0.78 0.06 85)" />

        <div className="relative flex items-center justify-end">
          <Logo size={20} color="oklch(0.96 0.005 85)" />
        </div>

        <div className="relative max-w-[460px]">
          <div
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.08em]"
            style={{ borderColor: 'oklch(0.42 0.03 250)', color: 'oklch(0.88 0.01 85)' }}
          >
            <Icon.lock className="w-3.5 h-3.5 text-accent" /> Invitation only
          </div>
          <h1 className="font-display mt-4 text-[52px] font-extrabold leading-[1.02] tracking-[-0.03em]" style={{ color: 'oklch(0.97 0.005 85)' }}>
            One standard,
            <br />
            every sport.
          </h1>
          <p className="mt-4 text-[14.5px] leading-relaxed" style={{ color: 'oklch(0.80 0.01 85)' }}>
            ProRef is a closed education platform for accredited referees — match clips, rule tests and
            federation-graded feedback, built for officials across multiple sports.
          </p>

          <div className="mt-7">
            <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.1em]" style={{ color: 'oklch(0.66 0.012 85)' }}>
              Covering
            </div>
            <div className="flex flex-wrap gap-2">
              {SPORTS.map((s) => (
                <span
                  key={s}
                  className="inline-flex h-[30px] items-center rounded-full border px-3 text-[12.5px] font-medium"
                  style={{ borderColor: 'oklch(0.40 0.03 250)', background: 'oklch(0.24 0.03 250 / 0.5)', color: 'oklch(0.90 0.008 85)' }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>

        <PartnerLogos partners={LOGIN_PARTNERS} label="Used by" tone="dark" className="relative" />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-9">
        <div className="w-full max-w-[380px]">
          <h2 className="mb-1.5 text-2xl tracking-[-0.012em] text-ink">Sign in to ProRef</h2>
          <p className="mb-6 text-[13.5px] text-muted">
            Access by invitation — use the federation account you were enrolled with.
          </p>

          <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
            <Field label="Email" htmlFor="email" error={errors.email?.message}>
              <Input id="email" type="email" autoComplete="email" placeholder="marko@hrs.hr" {...register('email')} />
            </Field>

            <Field
              label="Password"
              htmlFor="password"
              error={errors.password?.message}
              action={
                <Link to="/forgot-password" className="text-[11.5px] font-medium text-accent hover:underline">
                  Forgot?
                </Link>
              }
            >
              <Input id="password" type="password" autoComplete="current-password" placeholder="••••••••" {...register('password')} />
            </Field>

            <label className="flex items-center gap-2 text-[12.5px] text-ink-3">
              <input type="checkbox" defaultChecked /> Remember this device
            </label>

            <Button type="submit" variant="accent" size="lg" block disabled={isSubmitting} className="mt-1">
              {isSubmitting ? <Spinner className="w-4 h-4" /> : <>Sign in <Icon.arrow className="w-3.5 h-3.5" /></>}
            </Button>
          </form>

          {/* Invitation-only note */}
          <div className="mt-6 flex items-start gap-2.5 rounded-sm border border-line bg-bg-2 p-3.5">
            <div className="grid h-[26px] w-[26px] flex-none place-items-center rounded-sm bg-accent-tint text-accent">
              <Icon.lock className="w-3.5 h-3.5" />
            </div>
            <div>
              <div className="text-[12.5px] font-semibold text-ink">Invitation only</div>
              <p className="mt-0.5 text-[12px] text-muted">
                ProRef accounts are issued by your federation administrator. No invite yet? Ask your
                federation to add you — there's no public sign-up.
              </p>
            </div>
          </div>

          {/* The art panel carrying this strip is hidden below `md`, so repeat it under the form there. */}
          <PartnerLogos partners={LOGIN_PARTNERS} label="Used by" className="mt-7 md:hidden" />
        </div>
      </div>
    </div>
  );
}
